import type { Column, Header, Table } from '@tanstack/react-table'
import {
	type CSSProperties,
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import type { DataTableColumn } from './types'

type DataTableCSSVars = CSSProperties & Record<`--${string}`, string | number>

/** Width kept clear of the viewport edge when a pinned column is resized, so a slice of the opposite content stays visible. */
const PINNED_RESIZE_EDGE_RESERVE = 64

interface UseDataTableSizingArgs<TData> {
	table: Table<TData>
	columns: DataTableColumn<TData>[]
	containerRef: RefObject<HTMLDivElement | null>
}

interface ResizeColumnStart {
	id: string
	startSize: number
	minSize: number
	maxSize: number
}

interface LiveStyleElement {
	element: HTMLElement
	left: string
	right: string
	width: string
}

interface LiveResizeElements {
	columns: Map<string, LiveStyleElement[]>
	containerWidth: number
	tableWidth: LiveStyleElement[]
}

function clampFlexColumns<TData>(
	columns: Column<TData, unknown>[],
	flexMap: Record<string, number>,
	remainingWidth: number,
	sizing: Record<string, number>
) {
	const totalFlex = columns.reduce(
		(total, column) => total + flexMap[column.id],
		0
	)
	let availableWidth = remainingWidth
	const unclampedColumns: Column<TData, unknown>[] = []
	let hasClamp = false

	if (totalFlex <= 0) {
		return {
			hasClamp,
			remainingWidth: availableWidth,
			totalFlex,
			unclampedColumns
		}
	}

	for (const column of columns) {
		const minSize = column.columnDef.minSize ?? 50
		const maxSize = column.columnDef.maxSize ?? Number.POSITIVE_INFINITY
		const width = (remainingWidth * flexMap[column.id]) / totalFlex
		if (width < minSize) {
			sizing[column.id] = minSize
			availableWidth -= minSize
			hasClamp = true
		} else if (width > maxSize) {
			sizing[column.id] = maxSize
			availableWidth -= maxSize
			hasClamp = true
		} else {
			unclampedColumns.push(column)
		}
	}

	return {
		hasClamp,
		remainingWidth: availableWidth,
		totalFlex,
		unclampedColumns
	}
}

function computeFlexFillSizing<TData>(
	visibleColumns: Column<TData, unknown>[],
	flexMap: Record<string, number>,
	manualSet: Set<string>,
	containerWidth: number
) {
	/** Re-run flex distribution after each column that hits min/max is fixed. */
	let fixedSum = 0
	let flexColumns: Column<TData, unknown>[] = []
	for (const column of visibleColumns) {
		if (flexMap[column.id] !== undefined && !manualSet.has(column.id)) {
			flexColumns.push(column)
		} else {
			fixedSum += column.getSize()
		}
	}
	if (flexColumns.length === 0) {
		return null
	}

	let remainingWidth = Math.max(0, containerWidth - fixedSum)
	const sizing: Record<string, number> = {}
	while (flexColumns.length > 0) {
		const pass = clampFlexColumns(
			flexColumns,
			flexMap,
			remainingWidth,
			sizing
		)
		if (!pass.hasClamp) {
			for (const column of pass.unclampedColumns) {
				sizing[column.id] =
					(pass.remainingWidth * flexMap[column.id]) / pass.totalFlex
			}
			break
		}
		flexColumns = pass.unclampedColumns
		remainingWidth = pass.remainingWidth
	}

	return Object.keys(sizing).length === 0 ? null : sizing
}

function getLiveColumnSize<TData>(
	column: Column<TData, unknown>,
	sizing: Record<string, number> | null
) {
	return sizing?.[column.id] ?? column.getSize()
}

function getLiveTotalSize<TData>(
	columns: Column<TData, unknown>[],
	sizing: Record<string, number> | null
) {
	let size = 0
	for (const column of columns) {
		size += getLiveColumnSize(column, sizing)
	}
	return size
}

function getResizeSizing(
	startColumns: ResizeColumnStart[],
	startSize: number,
	startOffset: number,
	clientX: number,
	direction: 1 | -1
) {
	/** Header drags resize every leaf column under that header proportionally. */
	const deltaOffset = (clientX - startOffset) * direction
	const deltaPercentage = Math.max(deltaOffset / startSize, -0.999_999)
	const sizing: Record<string, number> = {}
	for (const column of startColumns) {
		const nextSize =
			Math.round(
				Math.min(
					Math.max(
						column.minSize,
						column.startSize + column.startSize * deltaPercentage
					),
					column.maxSize
				) * 100
			) / 100
		sizing[column.id] = nextSize
	}
	return sizing
}

function capPinnedResizeGrowth<TData>(args: {
	header: Header<TData, unknown>
	startColumns: ResizeColumnStart[]
	leftPinnedColumns: Column<TData, unknown>[]
	rightPinnedColumns: Column<TData, unknown>[]
	containerWidth: number
}) {
	/**
	 * Pinned columns are sticky and overlap the center as they grow, so an
	 * uncapped resize lets them cover the whole viewport (including the opposite
	 * pinned region) with no scroll to recover. Cap the resized pinned header so
	 * all pinned columns together never exceed the container, keeping the
	 * opposite side visible.
	 */
	if (args.header.column.getIsPinned() === false) {
		return
	}
	const pinnedColumns = [
		...args.leftPinnedColumns,
		...args.rightPinnedColumns
	]
	const totalPinnedWidth = pinnedColumns.reduce(
		(total, column) => total + column.getSize(),
		0
	)
	const headerWidth = args.startColumns.reduce(
		(total, column) => total + column.startSize,
		0
	)
	const otherPinnedWidth = totalPinnedWidth - headerWidth
	const budget = Math.max(
		args.containerWidth - otherPinnedWidth - PINNED_RESIZE_EDGE_RESERVE,
		headerWidth
	)
	// `budget >= headerWidth`, so the scale never shrinks a leaf below its start.
	const scale = budget / headerWidth
	for (const column of args.startColumns) {
		column.maxSize = Math.min(column.maxSize, column.startSize * scale)
	}
}

function getLiveStyleElements(root: HTMLElement, selector: string) {
	return Array.from(root.querySelectorAll<HTMLElement>(selector)).map(
		(element) => ({
			element,
			left: element.style.left,
			right: element.style.right,
			width: element.style.width
		})
	)
}

function getLiveResizeElements(root: HTMLElement, columnIds: string[]) {
	const columns = new Map<string, LiveStyleElement[]>()
	for (const columnId of columnIds) {
		columns.set(
			columnId,
			getLiveStyleElements(
				root,
				`[data-column-id="${CSS.escape(columnId)}"]`
			)
		)
	}

	return {
		columns,
		containerWidth: root.clientWidth,
		tableWidth: getLiveStyleElements(root, '[data-table-width="true"]')
	}
}

function setLiveStyleWidth(
	elements: LiveStyleElement[] | undefined,
	width: number
) {
	if (elements === undefined) {
		return
	}
	const value = `${width}px`
	for (const item of elements) {
		item.element.style.width = value
	}
}

function setLiveStyleSide(
	elements: LiveStyleElement[] | undefined,
	side: 'left' | 'right',
	offset: number
) {
	if (elements === undefined) {
		return
	}
	const value = `${offset}px`
	for (const item of elements) {
		item.element.style[side] = value
	}
}

function restoreLiveResizeStyles(elements: LiveResizeElements) {
	/** Remove the temporary pointermove styles before React takes over again. */
	const restore = (items: Iterable<LiveStyleElement>) => {
		for (const item of items) {
			item.element.style.width = item.width
			item.element.style.left = item.left
			item.element.style.right = item.right
		}
	}
	restore(elements.tableWidth)
	for (const items of elements.columns.values()) {
		restore(items)
	}
}

function applyLiveResizeStyles<TData>(args: {
	elements: LiveResizeElements
	leftPinnedColumns: Column<TData, unknown>[]
	rightPinnedColumns: Column<TData, unknown>[]
	sizing: Record<string, number>
	startColumns: ResizeColumnStart[]
	startTotalSize: number
}) {
	for (const column of args.startColumns) {
		setLiveStyleWidth(
			args.elements.columns.get(column.id),
			args.sizing[column.id] ?? column.startSize
		)
	}
	let totalSize = args.startTotalSize
	for (const column of args.startColumns) {
		totalSize +=
			(args.sizing[column.id] ?? column.startSize) - column.startSize
	}
	setLiveStyleWidth(
		args.elements.tableWidth,
		Math.max(totalSize, args.elements.containerWidth)
	)

	let leftOffset = 0
	for (const column of args.leftPinnedColumns) {
		setLiveStyleSide(
			args.elements.columns.get(column.id),
			'left',
			leftOffset
		)
		leftOffset += getLiveColumnSize(column, args.sizing)
	}

	let rightOffset = 0
	for (let i = args.rightPinnedColumns.length - 1; i >= 0; i -= 1) {
		const column = args.rightPinnedColumns[i]
		if (column === undefined) {
			continue
		}
		setLiveStyleSide(
			args.elements.columns.get(column.id),
			'right',
			rightOffset
		)
		rightOffset += getLiveColumnSize(column, args.sizing)
	}
}

function applyColumnSizeVars<TData>(
	columns: Column<TData, unknown>[],
	root: HTMLElement,
	sizing: Record<string, number> | null
) {
	for (const column of columns) {
		root.style.setProperty(
			`--col-${column.id}-size`,
			String(getLiveColumnSize(column, sizing))
		)
	}
	root.style.setProperty(
		'--col-total-size',
		String(getLiveTotalSize(columns, sizing))
	)
}

function addPinOffsetVars(
	vars: DataTableCSSVars,
	side: 'left' | 'right',
	ids: string[]
) {
	const terms: string[] = []
	if (side === 'left') {
		for (const id of ids) {
			vars[`--col-pin-${side}-${id}-offset`] =
				terms.length === 0
					? '0px'
					: `calc((${terms.join(' + ')}) * 1px)`
			terms.push(`var(--col-${id}-size)`)
		}
		return
	}
	for (let i = ids.length - 1; i >= 0; i -= 1) {
		const id = ids[i]
		if (id === undefined) {
			continue
		}
		vars[`--col-pin-${side}-${id}-offset`] =
			terms.length === 0 ? '0px' : `calc((${terms.join(' + ')}) * 1px)`
		terms.push(`var(--col-${id}-size)`)
	}
}

export function useDataTableSizing<TData>(args: UseDataTableSizingArgs<TData>) {
	const { table, columns, containerRef } = args

	const hasFlexColumns = columns.some((column) => column.flex !== undefined)
	const [containerWidth, setContainerWidth] = useState(0)
	const manualFlexColumnsRef = useRef<Set<string>>(new Set())
	const isResizingRef = useRef(false)
	const liveSizingRef = useRef<Record<string, number> | null>(null)
	const [flexResetVersion, setFlexResetVersion] = useState(0)

	useLayoutEffect(() => {
		const el = containerRef.current
		if (!(el && hasFlexColumns)) {
			return
		}
		let settleId: number | null = null
		let lastWidth = el.clientWidth
		setContainerWidth(lastWidth)

		const applyLiveFlexSizing = () => {
			if (isResizingRef.current) {
				return
			}
			const nextWidth = el.clientWidth
			if (nextWidth === lastWidth) {
				return
			}
			lastWidth = nextWidth
			const flexMap: Record<string, number> = {}
			for (const column of columns) {
				if (column.flex !== undefined) {
					flexMap[column.id] = column.flex
				}
			}
			const visibleColumns = table.getVisibleLeafColumns()
			const sizing = computeFlexFillSizing(
				visibleColumns,
				flexMap,
				manualFlexColumnsRef.current,
				nextWidth
			)
			liveSizingRef.current = sizing
			applyColumnSizeVars(visibleColumns, el, sizing)
			if (settleId !== null) {
				window.clearTimeout(settleId)
			}
			settleId = window.setTimeout(() => {
				settleId = null
				setContainerWidth(lastWidth)
			}, 120)
		}

		const observer = new ResizeObserver(() => {
			applyLiveFlexSizing()
		})
		observer.observe(el)
		return () => {
			observer.disconnect()
			if (settleId !== null) {
				window.clearTimeout(settleId)
			}
		}
	}, [columns, containerRef, hasFlexColumns, table])

	useEffect(() => {
		const liveIds = new Set(columns.map((c) => c.id))
		for (const id of manualFlexColumnsRef.current) {
			if (!liveIds.has(id)) {
				manualFlexColumnsRef.current.delete(id)
			}
		}
		table.setColumnSizing((prev) => {
			let mutated = false
			const next: Record<string, number> = { ...prev }
			for (const id of Object.keys(next)) {
				if (!liveIds.has(id)) {
					delete next[id]
					mutated = true
				}
			}
			return mutated ? next : prev
		})
	}, [columns, table])

	function handleResetColumn(columnId: string) {
		manualFlexColumnsRef.current.delete(columnId)
		setFlexResetVersion((version) => version + 1)
	}

	function handleResizeStart(
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) {
		const root = containerRef.current
		if (!root) {
			return
		}

		event.preventDefault()
		const handle = event.currentTarget
		const pointerId = event.pointerId
		const ownerDocument = handle.ownerDocument
		const ownerWindow = ownerDocument.defaultView
		const startSize = header.getSize()
		if (startSize <= 0) {
			return
		}

		const startColumns: ResizeColumnStart[] = header
			.getLeafHeaders()
			.map((leafHeader) => ({
				id: leafHeader.column.id,
				startSize: leafHeader.column.getSize(),
				minSize: leafHeader.column.columnDef.minSize ?? 50,
				maxSize:
					leafHeader.column.columnDef.maxSize ??
					Number.POSITIVE_INFINITY
			}))
		const leftPinnedColumns = table.getLeftVisibleLeafColumns()
		const rightPinnedColumns = table.getRightVisibleLeafColumns()
		const visibleColumns = table.getVisibleLeafColumns()
		const startTotalSize = getLiveTotalSize(visibleColumns, null)

		capPinnedResizeGrowth({
			header,
			startColumns,
			leftPinnedColumns,
			rightPinnedColumns,
			containerWidth: root.clientWidth
		})

		const flexColumnIds = new Set(
			columns.flatMap((column) =>
				column.flex === undefined ? [] : [column.id]
			)
		)
		for (const column of startColumns) {
			if (flexColumnIds.has(column.id)) {
				manualFlexColumnsRef.current.add(column.id)
			}
		}
		/** Cache resize targets once; pointermove only mutates these elements. */
		const liveElements = getLiveResizeElements(root, [
			...new Set([
				...startColumns.map((column) => column.id),
				...leftPinnedColumns.map((column) => column.id),
				...rightPinnedColumns.map((column) => column.id)
			])
		])
		const isResizeLtr = table.options.columnResizeDirection !== 'rtl'
		/**
		 * Right-pinned columns stick to the right edge, so the grab handle sits on
		 * their inner (left) edge and growing them means dragging inward (left).
		 * Invert the drag direction so they expand toward the center like AG Grid.
		 */
		const growsWithRightDrag =
			header.column.getIsPinned() === 'right' ? !isResizeLtr : isResizeLtr
		const direction = growsWithRightDrag ? 1 : -1
		const startOffset = event.clientX
		let finished = false
		let frameId: number | null = null
		let latestSizing = getResizeSizing(
			startColumns,
			startSize,
			startOffset,
			startOffset,
			direction
		)

		isResizingRef.current = true
		liveSizingRef.current = latestSizing
		handle.dataset.resizing = 'true'
		handle.setPointerCapture(pointerId)

		const applyLatestSizing = () => {
			frameId = null
			applyLiveResizeStyles({
				elements: liveElements,
				leftPinnedColumns,
				rightPinnedColumns,
				sizing: latestSizing,
				startColumns,
				startTotalSize
			})
		}

		const scheduleResize = (clientX: number) => {
			latestSizing = getResizeSizing(
				startColumns,
				startSize,
				startOffset,
				clientX,
				direction
			)
			liveSizingRef.current = latestSizing
			if (frameId !== null) {
				return
			}
			frameId = window.requestAnimationFrame(applyLatestSizing)
		}

		const removeListeners = () => {
			ownerDocument.removeEventListener('pointermove', onPointerMove)
			ownerDocument.removeEventListener('pointerup', onPointerUp)
			ownerDocument.removeEventListener('pointercancel', onPointerCancel)
			ownerWindow?.removeEventListener('blur', onBlur)
		}

		const finishResize = (clientX: number | undefined) => {
			if (finished) {
				return
			}
			finished = true
			if (clientX !== undefined) {
				latestSizing = getResizeSizing(
					startColumns,
					startSize,
					startOffset,
					clientX,
					direction
				)
				liveSizingRef.current = latestSizing
			}
			if (frameId !== null) {
				window.cancelAnimationFrame(frameId)
				frameId = null
			}
			applyLiveResizeStyles({
				elements: liveElements,
				leftPinnedColumns,
				rightPinnedColumns,
				sizing: latestSizing,
				startColumns,
				startTotalSize
			})
			applyColumnSizeVars(visibleColumns, root, latestSizing)
			restoreLiveResizeStyles(liveElements)
			if (handle.hasPointerCapture(pointerId)) {
				handle.releasePointerCapture(pointerId)
			}
			removeListeners()
			isResizingRef.current = false
			liveSizingRef.current = null
			delete handle.dataset.resizing
			table.setColumnSizing((previous) => ({
				...previous,
				...latestSizing
			}))
		}

		function onPointerMove(pointerEvent: PointerEvent) {
			if (pointerEvent.pointerId !== pointerId) {
				return
			}
			if (pointerEvent.cancelable) {
				pointerEvent.preventDefault()
			}
			scheduleResize(pointerEvent.clientX)
		}

		function onPointerUp(pointerEvent: PointerEvent) {
			if (pointerEvent.pointerId !== pointerId) {
				return
			}
			finishResize(pointerEvent.clientX)
		}

		function onPointerCancel(pointerEvent: PointerEvent) {
			if (pointerEvent.pointerId !== pointerId) {
				return
			}
			finishResize(undefined)
		}

		function onBlur() {
			finishResize(undefined)
		}

		ownerDocument.addEventListener('pointermove', onPointerMove)
		ownerDocument.addEventListener('pointerup', onPointerUp)
		ownerDocument.addEventListener('pointercancel', onPointerCancel)
		ownerWindow?.addEventListener('blur', onBlur)
	}

	const leftPinnedColumns = table.getLeftVisibleLeafColumns()
	const centerColumns = table.getCenterVisibleLeafColumns()
	const rightPinnedColumns = table.getRightVisibleLeafColumns()
	const leafColumns = [
		...leftPinnedColumns,
		...centerColumns,
		...rightPinnedColumns
	]
	/**
	 * Signature of the visible columns (id + pinning, in order). Threaded into the
	 * body so rows refresh when columns change: TanStack caches the row model on
	 * `data` alone, so without it cells keep stale column defs after a column change.
	 */
	const columnsKey = leafColumns
		.map((column) => `${column.id}:${column.getIsPinned() || ''}`)
		.join('|')
	const flexFillKey =
		columnsKey === '' ? '' : `${columnsKey}:${flexResetVersion}`

	useLayoutEffect(() => {
		/** Fill free width before paint, but never fight an active drag resize. */
		if (!hasFlexColumns || containerWidth === 0 || flexFillKey === '') {
			return
		}
		if (isResizingRef.current) {
			return
		}
		const flexMap: Record<string, number> = {}
		for (const column of columns) {
			if (column.flex !== undefined) {
				flexMap[column.id] = column.flex
			}
		}
		const sizing = computeFlexFillSizing(
			table.getVisibleLeafColumns(),
			flexMap,
			manualFlexColumnsRef.current,
			containerWidth
		)
		if (sizing === null) {
			return
		}
		const currentSizing = table.getState().columnSizing
		let changed = false
		for (const id of Object.keys(sizing)) {
			if (Math.abs((currentSizing[id] ?? 0) - sizing[id]) >= 0.5) {
				changed = true
				break
			}
		}
		liveSizingRef.current = null
		if (changed) {
			table.setColumnSizing((previous) => ({ ...previous, ...sizing }))
		}
	}, [columns, containerWidth, flexFillKey, hasFlexColumns, table])

	const liveSizing = liveSizingRef.current
	const columnSizeVars: DataTableCSSVars = {}
	for (const column of leafColumns) {
		columnSizeVars[`--col-${column.id}-size`] = getLiveColumnSize(
			column,
			liveSizing
		)
	}
	columnSizeVars['--col-total-size'] = getLiveTotalSize(
		leafColumns,
		liveSizing
	)
	addPinOffsetVars(
		columnSizeVars,
		'left',
		leftPinnedColumns.map((column) => column.id)
	)
	addPinOffsetVars(
		columnSizeVars,
		'right',
		rightPinnedColumns.map((column) => column.id)
	)

	return {
		columnSizeVars,
		columnsKey,
		leafColumns,
		onResetColumn: handleResetColumn,
		onResizeStart: handleResizeStart
	}
}
